import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'preact/hooks';
import { AlmostEqual } from '../../math';
import { sendVideoMetadata } from '../../teads-api';
import { useAdLoaded } from './useAdLoaded';
import { useComponentLoaded, useComponentsLoaded } from './useComponentsLoaded';
import { useOnImageLoad } from './useOnImageLoad';
import { useResizeObserver } from './useResizeObserver';

export function useUpdateSlotSize(ad: HTMLElement | null, filter?: (adWidth: number, adHeight: number) => boolean) {
  const setAdLoaded = useAdLoaded();
  const adRefSize = useResizeObserver(ad);

  const filterResult = useMemo(
    () => filter?.(adRefSize.width, adRefSize.height) ?? true,
    [adRefSize.width, adRefSize.height, filter]
  );

  useEffect(() => {
    if (adRefSize.width < 2 || adRefSize.height < 2) return;
    if (!filterResult) return;

    sendVideoMetadata({
      width: Math.floor(adRefSize.width),
      height: Math.floor(adRefSize.height),
    });

    setAdLoaded();
  }, [adRefSize.width, adRefSize.height, filterResult]);
}

export function useUpdateSlotSizeWithVideoSlot(ad: HTMLElement | null, videoSlot: HTMLVideoElement) {
  const [isLoaded, setLoaded] = useState(false);
  useLayoutEffect(() => {
    const onLoadedMetadata = () => setLoaded(true);

    if (videoSlot.readyState > 0) {
      onLoadedMetadata();
    } else {
      videoSlot.addEventListener('loadedmetadata', onLoadedMetadata);
      return () => videoSlot.removeEventListener('loadedmetadata', onLoadedMetadata);
    }
  }, []);
  const filter = useCallback(() => {
    const vs = videoSlot as HTMLVideoElement;

    return isLoaded && !AlmostEqual(vs.videoWidth, 0) && !AlmostEqual(vs.videoHeight, 0);
  }, [isLoaded, videoSlot]);
  useUpdateSlotSize(ad, filter);
}

export function useUpdateSlotSizeWithMainImage(
  ad: HTMLElement | null,
  mainImage: HTMLImageElement | null,
  mainImageSize: { width: number; height: number }
) {
  const onImageLoaded = useComponentLoaded('Main Image');
  const areComponentsLoaded = useComponentsLoaded();

  useLayoutEffect(() => {
    if (mainImage?.complete) {
      onImageLoaded();
    }
  }, [mainImage?.complete]);
  useOnImageLoad(mainImage, onImageLoaded);

  const filter = useCallback(
    (adWidth: number, adHeight: number) => {
      return (
        areComponentsLoaded &&
        mainImageSize.width > 0 &&
        mainImageSize.height > 0 &&
        (adWidth > mainImageSize.width || AlmostEqual(adWidth, mainImageSize.width)) &&
        (adHeight > mainImageSize.height || AlmostEqual(adHeight, mainImageSize.height))
      );
    },
    [areComponentsLoaded, mainImageSize.width, mainImageSize.height]
  );

  useUpdateSlotSize(ad, filter);
}
